import { ArrowRight, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { offers, tiers } from "./content"
import { toneBg, type SectionTone } from "./section-tone"

export function Offers({ tone = "tint" }: { tone?: SectionTone }) {
  return (
    <section
      id="offers"
      data-section="offers"
      className={cn("scroll-mt-24 border-b border-hairline", toneBg[tone])}
    >
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <span className="section-eyebrow">{offers.eyebrow}</span>
        <h2 className="section-title mt-3 max-w-2xl">{offers.title}</h2>

        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={cn(
                "flex flex-col rounded-2xl border bg-page p-6 shadow-sm sm:p-8",
                tier.featured ? "border-accent ring-1 ring-accent" : "border-hairline",
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <h3>{tier.name}</h3>
                {tier.badge ? (
                  <span className="badge badge-accent">{tier.badge}</span>
                ) : null}
              </div>
              <ul className="mt-6 space-y-3">
                {tier.includes.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <Check
                      aria-hidden="true"
                      className="mt-0.5 h-4 w-4 shrink-0 text-accent-display"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {tier.note ? (
                <p className="meta mt-6 border-t border-hairline pt-4">{tier.note}</p>
              ) : null}

              {tier.sample ? (
                // Same animated gradient as the case-study CTA. self-start
                // keeps it to its content width — the card is a flex column, so
                // without it the button would stretch the full card width and
                // outweigh the actual CTA at the bottom.
                <Button asChild className="cta-gradient mt-5 self-start">
                  <a
                    href={tier.sample.href}
                    data-track-click={tier.sample.trackId}
                    data-sound="cta_click"
                  >
                    {tier.sample.label}
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  </a>
                </Button>
              ) : null}

              <div
                className={cn(
                  // mt-auto pins the CTA row to the bottom of the card so the
                  // two cards' buttons align even though only one carries a
                  // note line. pt-8 supplies the spacing mt-8 used to.
                  "mt-auto flex flex-wrap items-center gap-3 pt-8",
                  tier.price ? "justify-between" : "justify-end",
                )}
              >
                {tier.price ? <span className="price">{tier.price}</span> : null}
                <Button
                  asChild
                  variant={tier.featured ? "primary" : "secondary"}
                  className={tier.featured ? undefined : "offer-btn-outline"}
                >
                  <a href="#apply" data-track-click={tier.trackId} data-sound="cta_click">
                    {tier.ctaLabel}
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
